/**
 * Sealed-PDF download hooks. Owner endpoint and recipient (token) endpoint
 * each get their own hook because the auth/transport differ — owner uses
 * the bearer token, recipient uses the path-token.
 *
 * Polling automatically stops once a URL has been returned. The presigned
 * URL is valid for 5 minutes; we don't re-presign on a loop because each
 * call consumes the per-IP rate-limit budget on the public surface — a
 * recipient leaving the tab open for an hour would hit 429 within minutes
 * if we polled blindly. Callers that need a fresh URL can call `refetch()`.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface SealedDownloadOptions {
  enabled?: boolean;
  /** Poll interval while no URL is in cache. Disabled (false) by default. */
  refetchInterval?: number | false;
}

export function useOwnerSealedDownload(
  envelopeId: string | null | undefined,
  options?: SealedDownloadOptions,
) {
  return useQuery({
    queryKey: [...queryKeys.documentSign.envelope(envelopeId ?? '__none__'), 'sealed'],
    queryFn: () => api.getDocumentSignSealedDownloadUrl(envelopeId as string),
    enabled: !!envelopeId && (options?.enabled ?? true),
    // Stop polling once we have a URL — otherwise we refetch every interval
    // forever, burning rate-limit budget and producing useless DB hits.
    refetchInterval: (query) => (query.state.data ? false : (options?.refetchInterval ?? false)),
    retry: false,
  });
}

export function useRecipientSealedDownload(
  token: string | null | undefined,
  options?: SealedDownloadOptions,
) {
  return useQuery({
    queryKey: [...queryKeys.documentSign.signingSession(token ?? '__none__'), 'sealed'],
    queryFn: () => api.getDocumentSignSigningSealedUrl(token as string),
    enabled: !!token && (options?.enabled ?? true),
    refetchInterval: (query) => (query.state.data ? false : (options?.refetchInterval ?? false)),
    retry: false,
  });
}

export function useResealEnvelope(envelopeId: string) {
  return useMutation({
    mutationFn: () => api.resealDocumentSignEnvelope(envelopeId),
  });
}
